// Visitor & Contractor module — API layer (RON-5466).
//
// THIS IS THE SWAP POINT. Every function returns the app's standard response envelope
// (`{ Data: { ResponseCode, Records } }`) and is `async`, exactly like the real Layer A
// wrappers. While `USE_VISITOR_MOCKS` is true the data comes from `visitorMockData.js`.
//
// When the endpoints exist, each body becomes the usual three lines and NOTHING in any screen
// changes:
//
//   export const GetVisits_api = (requestParams) => {
//     requestParams.AppVersion = AppConstant.AppVersion;
//     requestParams.Language = AppConstant.Language;
//     requestParams.DeviceId = AppConstant.DeviceID;
//     requestParams.OsVersion = AppConstant.OsVersion;
//     requestParams.ManufactureModel = AppConstant.ManufactureModel;
//     return post({ url: `${BASE_URL}${GetVisits_url}`, params: requestParams });
//   };
//
// The paths then go in `src/config/ApiConfig.js` alongside the other endpoints.

import {
  CONTRACTOR_DB,
  MOCK_SITES,
  MOCK_VISITS,
  USE_VISITOR_MOCKS,
} from "./visitorMockData.js";
import { VISIT_STATUS_FILTERS } from "./visitorApiMapper.js";
import { VISIT_STATUS } from "./visitorConstants.js";

// Small delay so loading states are actually exercised during design review instead of
// resolving in the same tick and never appearing.
const MOCK_LATENCY_MS = 300;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Matches the shape every screen already expects from `post(...)`.
const mockResponse = (records) => ({
  Data: {
    ResponseCode: 200,
    Records: records,
  },
});

// Failure envelope, same shape `handleApiUiResponse` reads. Codes in ORANGE_POPUP_CODES (409 here)
// surface as the app's alert popup rather than an error toast.
const mockFailure = (code, message) => ({
  Data: {
    ResponseCode: code,
    ErrorMessage: message,
  },
});

// ── Capabilities: the SERVER's answer, not the client's ──────────────────────────────────────
//
// RON-6002 §4.5: "`can_edit` and `can_cancel` are the server's answer. Use them. Do not derive
// them from `status`." The real API computes them from *status is `expected`* AND *you hold the
// capability for that kind of visit*, and sends them on every row.
//
// This function is the MOCK SERVER doing that job. It lives here — not in `visitorConstants.js`
// where it used to — precisely because it is server work: when `USE_VISITOR_MOCKS` goes false
// this function is deleted and `visitFromApi` reads the real flags instead. Nothing in any screen
// changes, because no screen derives anything any more.
//
// ONLY AN UPCOMING VISIT CAN BE CANCELLED (Q5, decided 23-Sep-2026 in favour of the contract).
//
// This module previously let a host cancel a guest who had already arrived, and treated that as
// also checking them out. The contract refuses it in two places — §4.5's rule for `can_cancel` is
// *status is `expected`*, and §4.8 answers "409 if already cancelled or the visitor has arrived" —
// so the real server would have refused an action the UI offered, and the host would have met a
// 409 popup on a button that looked live.
//
// A guest who is on site gets checked out, not cancelled, and that is reception's job.
const CANCELLABLE_STATUSES = [VISIT_STATUS.EXPECTED];

const deriveCapabilities = (visit) => ({
  canEdit: visit?.status === VISIT_STATUS.EXPECTED,
  canCancel: CANCELLABLE_STATUSES.includes(visit?.status),
});

// Every row leaving this module carries its capabilities, exactly as the real API's rows do.
const withCapabilities = (visit) =>
  visit == null ? visit : { ...visit, ...deriveCapabilities(visit) };

// Deep-ish clone so a screen mutating a row cannot corrupt the fixture for the next read —
// the real API would hand back fresh objects each call. Capabilities are stamped on the way out,
// so a status the store changed (a cancel) yields fresh flags rather than stale ones.
const cloneVisits = (visits) => visits.map(withCapabilities);

// Seeding is NOT stamped: the store holds raw rows and capabilities are computed at every read, so
// a cancel that changes `status` cannot leave a stale `canCancel: true` behind it.
const seedVisits = (visits) => visits.map((visit) => ({ ...visit }));

// ── Session store ────────────────────────────────────────────────────────────────
// Edit and cancel MUST persist for the length of the session. `CreateVisit_api` deliberately does
// not (see its note) because a new row has no visible home in a fixed fixture, but a cancel that
// leaves the row reading "Expected" in the list makes the flow look broken in design review.
//
// The store is a copy, so `visitorMockData.js` stays a pure fixture and a page reload resets
// everything. It re-seeds whenever the fixture's identity changes, which is what lets a test swap
// `MOCK_VISITS` between cases without needing a reset hook.
let visitStore = null;
let seededFrom = null;

const store = () => {
  if (visitStore === null || seededFrom !== MOCK_VISITS) {
    visitStore = seedVisits(MOCK_VISITS);
    seededFrom = MOCK_VISITS;
  }

  return visitStore;
};

// Accepts `VisitIDs: [...]`, a single `VisitID`, or a `GroupID`, so one function serves the single,
// group and bulk flows — and one real endpoint replaces it.
const resolveTargets = ({ VisitIDs, VisitID, GroupID }) => {
  if (GroupID) return store().filter((visit) => visit.group === GroupID);

  const ids = VisitIDs ?? (VisitID == null ? [] : [VisitID]);
  const wanted = new Set(Array.isArray(ids) ? ids : [ids]);

  return store().filter((visit) => wanted.has(visit.id));
};

const applyToStore = (visit, changes) => {
  const index = store().findIndex((candidate) => candidate.id === visit.id);
  if (index === -1) return null;

  store()[index] = { ...store()[index], ...changes };

  // Stamped on the way out, like every other read: a cancel changes `status`, so the row the
  // caller gets back must carry the NEW capabilities, not the ones it had a moment ago.
  return withCapabilities(store()[index]);
};

// Only the fields a caller actually sent. A bulk edit sends two or three keys and must not blank
// everything else, so `undefined` means "leave alone" rather than "clear".
const FIELD_MAP = {
  Name: "name",
  Email: "email",
  Company: "company",
  StartDate: "startDate",
  EndDate: "endDate",
  ArrivalTime: "arrivalTime",
  ExpectedOut: "expectedOut",
  Site: "site",
  Building: "building",
  HostName: "host",
  Type: "type",
};

const buildPatch = (requestParams) =>
  Object.entries(FIELD_MAP).reduce((patch, [param, field]) => {
    if (requestParams[param] !== undefined) patch[field] = requestParams[param];
    return patch;
  }, {});

// `handleApiUiResponse` shows the right toast/popup but returns nothing, so callers that need to
// branch on the outcome (e.g. only advance a wizard on success) must read the code themselves.
// 200 is the single success code that helper treats as success.
export const isSuccessfulResponse = (response) =>
  Number(response?.Data?.ResponseCode) === 200;

const notWired = (name) => {
  throw new Error(
    `Visitor APIs are not wired yet (${name}). Set USE_VISITOR_MOCKS = true in ` +
      "visitorMockData.js, or implement this function against the real endpoint."
  );
};

// GET the visits this employee hosts.
// Real endpoint TBD — the natural name is `GetVisits_api` / `MyVisits_api`.
// The list is PAGINATED and SEARCHED SERVER-SIDE (§4.4): `?q=&status=&page=&per_page=`.
//
// Both matter together. Once the list only holds one page, a client-side search would look through
// the rows already fetched and report "no visitors" for someone sitting on page 3 — which is the
// same lie §4.4 warns about for a mistyped status. So the term goes to the server.
//
// This mock does the filtering and slicing the real endpoint will do, rather than returning
// everything and letting the screen pretend, so the lazy-loading and empty states are exercised
// for real in design review.
export const DEFAULT_PAGE_SIZE = 25;

export const GetVisits_api = async (requestParams = {}) => {
  if (!USE_VISITOR_MOCKS) notWired("GetVisits_api");

  await delay(MOCK_LATENCY_MS);

  const page = Math.max(1, Number(requestParams.Page) || 1);
  const perPage = Math.max(1, Number(requestParams.PerPage) || DEFAULT_PAGE_SIZE);
  const term = String(requestParams.Query || "").trim().toLowerCase();
  const status = requestParams.Status || null;

  // §4.4 lists exactly five values and a wrong one is a 422, never an empty list — an empty list
  // reads as "you have no visitors" when the truth is a typo.
  if (status && !VISIT_STATUS_FILTERS.includes(status)) {
    return mockFailure(422, `Unknown status filter "${status}".`);
  }

  const matches = store().filter((visit) => {
    if (status && visit.status !== status) return false;
    if (!term) return true;

    // Name, company and the raw status, matching what the search placeholder promises. The real
    // endpoint searches name, email and company server-side.
    return [visit.name, visit.company, visit.email, visit.status]
      .filter(Boolean)
      .some((field) => String(field).toLowerCase().includes(term));
  });

  const start = (page - 1) * perPage;

  return mockResponse({
    Visits: cloneVisits(matches.slice(start, start + perPage)),
    // The count of everything that matched, NOT of this page — it is what tells the screen
    // whether there is more to load.
    Total: matches.length,
    Page: page,
    PerPage: perPage,
  });
};

// GET the sites (and their buildings) this employee may invite into.
// Real endpoint TBD. Scoped by CompanyID server-side; only `active` sites should come back.
export const GetVisitorSites_api = async () => {
  if (!USE_VISITOR_MOCKS) notWired("GetVisitorSites_api");

  await delay(MOCK_LATENCY_MS);

  return mockResponse({
    Sites: MOCK_SITES.map((site) => ({
      ...site,
      buildings: site.buildings.map((building) => ({ ...building })),
    })),
  });
};

// POST a new visit (one row per invited visitor, sharing a `group`).
//
// While mocked this only echoes back what it was handed, with server-assigned fields filled in —
// it does NOT mutate MOCK_VISITS, so the fixture stays stable for the next page load. The real
// endpoint will persist and the list will re-fetch.
export const CreateVisit_api = async (requestParams = {}) => {
  if (!USE_VISITOR_MOCKS) notWired("CreateVisit_api");

  await delay(MOCK_LATENCY_MS);

  const visitors = Array.isArray(requestParams.Visitors) ? requestParams.Visitors : [];

  return mockResponse({
    // The real API assigns these; the confirmation screen reads them back.
    Visits: visitors.map((visitor, index) => ({
      ...visitor,
      id: `v-new-${index}`,
      guid: `00000000-0000-4000-8000-00000000000${index}`,
      bookingCode: `VIS-NEW-${String(index + 1).padStart(3, "0")}`,
      status: "expected",
      // The real API sends capabilities on every row it returns, a freshly created one included.
      canEdit: true,
      canCancel: true,
    })),
    GroupId: "g-new",
  });
};

// PATCH one or more existing visits.
//
// One function for all three edit flows — single (`VisitID`), group (`GroupID`) and bulk
// (`VisitIDs`) — because they differ only in which rows they target. The real endpoint should keep
// that shape so bulk edit stays one round trip instead of N.
//
// Only the fields present in `requestParams` are written; see `buildPatch`.
export const UpdateVisit_api = async (requestParams = {}) => {
  if (!USE_VISITOR_MOCKS) notWired("UpdateVisit_api");

  await delay(MOCK_LATENCY_MS);

  const targets = resolveTargets(requestParams);

  if (targets.length === 0) {
    return mockFailure(404, "No matching visit was found.");
  }

  const patch = buildPatch(requestParams);
  const updated = targets
    .map((visit) => applyToStore(visit, patch))
    .filter(Boolean);

  return mockResponse({ Visits: updated, UpdatedCount: updated.length });
};

// Cancel one or more visits.
//
// ONLY AN UPCOMING VISIT (Q5, 23-Sep-2026). A guest who has already arrived is checked out by
// reception, not cancelled by their host — §4.8 answers 409 once the visitor has arrived, so
// offering it would have meant a 409 popup on a button that looked live.
//
// Rows that cannot be cancelled (already cancelled, checked out, no-show, arrived) are skipped
// rather than
// erroring the whole batch: a bulk cancel over a mixed selection should still do the work it can.
// If nothing at all was cancellable that IS an error, otherwise the UI would report success for a
// no-op.
export const CancelVisit_api = async (requestParams = {}) => {
  if (!USE_VISITOR_MOCKS) notWired("CancelVisit_api");

  await delay(MOCK_LATENCY_MS);

  const targets = resolveTargets(requestParams);

  if (targets.length === 0) {
    return mockFailure(404, "No matching visit was found.");
  }

  // The SERVER decides this, so it derives the answer itself rather than calling the screens'
  // predicate — that predicate now just reads the flag this function is about to stamp.
  const cancellable = targets.filter((visit) => deriveCapabilities(visit).canCancel);

  if (cancellable.length === 0) {
    // 409 is in ORANGE_POPUP_CODES, so this surfaces as the app's alert popup.
    return mockFailure(409, "These visits can no longer be cancelled.");
  }

  const cancelled = cancellable
    .map((visit) => applyToStore(visit, { status: VISIT_STATUS.CANCELLED }))
    .filter(Boolean);

  return mockResponse({
    Visits: cancelled,
    CancelledCount: cancelled.length,
    // Rows the caller asked for but that were already closed, or whose guest has arrived. §4.9
    // reports the same thing as `skipped`, so a mixed selection still does the work it can and
    // says what it left alone.
    SkippedCount: targets.length - cancellable.length,
  });
};

// ── Contractor vetting (Phase 4) ─────────────────────────────────────────────────

// Look a contractor up in the vetting register by email.
//
// A MISS IS NOT AN ERROR. "This person is not on the register" is the single most common outcome
// for a host inviting someone new, and it is the entry point to the request-vetting flow — so it
// returns 200 with `Found: false` rather than a 404 the screen would have to translate back into
// a normal state. Only a malformed request is an error.
//
// Email is matched case-insensitively and trimmed: hosts paste addresses out of Outlook, and
// "Declan@Murphyelectrical.ie " must find the same record.
export const LookupContractor_api = async (requestParams = {}) => {
  if (!USE_VISITOR_MOCKS) notWired("LookupContractor_api");

  await delay(MOCK_LATENCY_MS);

  const email = String(requestParams.Email || "").trim().toLowerCase();

  if (!email) return mockFailure(400, "An email address is required.");

  const record = CONTRACTOR_DB.find(
    (candidate) => candidate.email.toLowerCase() === email
  );

  if (!record) return mockResponse({ Found: false, Contractor: null });

  return mockResponse({
    Found: true,
    // Cloned, documents included, so a screen cannot mutate the register.
    Contractor: {
      ...record,
      documents: (record.documents || []).map((document) => ({ ...document })),
    },
  });
};

// Ask facilities to vet someone who is not on the register yet.
//
// This does NOT create a visit. Vetting is a separate approval that has to complete first, so the
// host gets a "request sent" confirmation and comes back to book once it clears. Conflating the
// two would let an unvetted contractor onto a site by accident.
export const RequestContractorVetting_api = async (requestParams = {}) => {
  if (!USE_VISITOR_MOCKS) notWired("RequestContractorVetting_api");

  await delay(MOCK_LATENCY_MS);

  const email = String(requestParams.Email || "").trim().toLowerCase();

  if (!email) return mockFailure(400, "An email address is required.");

  const existing = CONTRACTOR_DB.find(
    (candidate) => candidate.email.toLowerCase() === email
  );

  // 409 is in ORANGE_POPUP_CODES, so this surfaces as the app's alert popup rather than a toast.
  if (existing) {
    return mockFailure(409, "This contractor is already on the vetting register.");
  }

  return mockResponse({
    RequestId: "vet-req-new",
    Email: requestParams.Email,
    // What the host is waiting on. The real endpoint should return the same so the confirmation
    // can say who to chase.
    Status: "pending",
  });
};
